<template>
  <div class="question-groups-container">
    <div class="container py-5">
      <div class="row">
        <div class="col-12">
          <div class="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h1 class="display-heading with-highlight mb-2">
                <span class="icon icon-primary me-3">
                  <i class="fas fa-layer-group"></i>
                </span>
                Fragengruppen verwalten
              </h1>
              <p class="text-muted mb-0">
                Organisieren Sie Ihre Fragen in thematische Gruppen für bessere Übersichtlichkeit
              </p>
            </div>
            <button 
              class="btn btn-primary btn-lg btn-with-icon btn-glow-effect"
              @click="showCreateModal = true"
            >
              <span class="icon me-2"><i class="fas fa-plus"></i></span>
              Neue Gruppe erstellen
            </button>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-12">
          <Card animateIn="fade-in-up" class="card-modern card-with-table shadow-lg">
            <div v-if="loading" class="text-center py-5 content-loading card-loading-state">
              <div class="spinner-container mb-3">
                <div class="spinner-border text-primary" role="status">
                  <span class="visually-hidden">Wird geladen...</span>
                </div>
              </div>
              <p class="text-muted">Fragengruppen werden geladen...</p>
            </div>
            
            <div v-else-if="questionGroups.length === 0" class="text-center py-5 content-empty card-empty-state">
              <div class="empty-state-icon mb-4">
                <i class="fas fa-layer-group"></i>
              </div>
              <h5 class="empty-state-title mb-3">Noch keine Fragengruppen erstellt</h5>
              <p class="text-muted mb-4">Erstellen Sie Ihre erste Fragengruppe, um Fragen thematisch zu organisieren.</p>
              <button 
                class="btn btn-primary btn-with-icon btn-hover-effect"
                @click="showCreateModal = true"
              >
                <span class="icon me-2"><i class="fas fa-plus"></i></span>
                Erste Gruppe erstellen
              </button>
            </div>
            
            <div v-else class="question-groups-grid">
              <div 
                v-for="group in questionGroups" 
                :key="group.id" 
                class="question-group-card group-card-clickable"
                @click="goToDetails(group)"
                role="button"
                tabindex="0"
                @keydown.enter="goToDetails(group)"
              >
                <div class="group-header">
                  <div class="group-color-indicator" :style="{ backgroundColor: group.color }"></div>
                  <div class="group-info">
                    <h5 class="group-name d-flex align-items-center gap-2">
                      {{ group.name }}
                      <span class="badge rounded-pill bg-primary-soft">
                        <i class="fas fa-question-circle me-1"></i>{{ group.questions_count }}
                      </span>
                    </h5>
                    <p class="group-description mb-1">{{ group.description || 'Keine Beschreibung' }}</p>
                    <div class="group-meta text-muted small">
                      <i class="fas fa-calendar-alt me-1"></i>{{ formatDate(group.created_at) }}
                    </div>
                  </div>
                  <!-- actions moved to footer -->
                </div>
                <div class="group-stats d-flex align-items-center justify-content-between mt-2" @click.stop>
                  <div class="stat-item">
                    <i class="fas fa-question-circle text-primary"></i>
                    <span>{{ group.questions_count }} Fragen</span>
                  </div>
                  <div class="d-flex align-items-center gap-2">
                    <router-link 
                      class="btn btn-sm btn-outline-primary btn-with-icon btn-hover-effect"
                      :to="`/question-groups/${group.id}`"
                      title="Gruppe bearbeiten"
                      @click.stop
                    >
                      <span class="icon me-1"><i class="fas fa-edit"></i></span>
                      Bearbeiten
                    </router-link>
                    <button 
                      class="btn btn-sm btn-outline-danger btn-with-icon btn-hover-effect"
                      @click.stop="deleteGroup(group)"
                      title="Gruppe löschen"
                      :disabled="group.questions_count > 0"
                    >
                      <span class="icon me-1"><i class="fas fa-trash"></i></span>
                      Löschen
                    </button>

                  </div>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>

    <!-- Create/Edit Modal für Fragen in Gruppen wiederverwenden -->
    <CreateQuestionModal 
      ref="createGroupQuestionModal"
      :loading="questionLoading"
      :defaultPointsPerQuestion="defaultPointsPerQuestion"
      :questionType="currentQuestion.type"
      @save="saveGroupQuestion"
      @save-and-add-next="saveAndAddNextGroupQuestion"
      @reset="resetQuestionForm"
      @image-updated="handleQuestionImageUpdated"
      @image-removed="handleQuestionImageRemoved"
      @check-for-duplicates="checkForDuplicatesInGroup"
    />

    <EditQuestionModal 
      ref="editGroupQuestionModal"
      :loading="questionLoading"
      :defaultPointsPerQuestion="defaultPointsPerQuestion"
      @save="saveGroupQuestion"
      @reset="resetQuestionForm"
      @image-updated="handleQuestionImageUpdated"
      @image-removed="handleQuestionImageRemoved"
    />
  </div>
</template>

<script>
import Card from '@/components/ui/Card.vue';
import { useQuestionGroupsStore } from '@/stores/questionGroups';
import CreateQuestionModal from '@/views/tests/modals/CreateQuestionModal.vue';
import EditQuestionModal from '@/views/tests/modals/EditQuestionModal.vue';
import http from '@/services/http';

export default {
  name: 'QuestionGroups',
  components: {
    Card,
    CreateQuestionModal,
    EditQuestionModal
  },
  setup() {
    const questionGroupsStore = useQuestionGroupsStore();
    return { questionGroupsStore };
  },
  data() {
    return {
      loading: false,
      saving: false,
      showCreateModal: false,
      showEditModal: false,
      editingGroup: {
        id: null,
        name: '',
        description: '',
        color: '#007bff'
      },
      // Fragen-Management
      questionLoading: false,
      defaultPointsPerQuestion: 10,
      currentQuestion: {
        id: null,
        type: 'multiple_choice'
      },
      currentEditingGroup: null,
      questionImagePreview: null
    };
  },
  computed: {
    questionGroups() {
      return this.questionGroupsStore.getAllQuestionGroups;
    }
  },
  async created() {
    await this.loadQuestionGroups();
  },
  methods: {
    goToDetails(group) {
      if (!group?.id) return;
      this.$router.push(`/question-groups/${group.id}`);
    },
    async loadQuestionGroups() {
      this.loading = true;
      try {
        await this.questionGroupsStore.fetchQuestionGroups();
        // Fragen je Gruppe laden
        const groups = this.questionGroupsStore.getAllQuestionGroups;
        for (const g of groups) {
          this.$set ? this.$set(g, '__questionsLoading', true) : (g.__questionsLoading = true);
          try {
            const qs = await this.questionGroupsStore.fetchQuestionsByGroup(g.id);
            g.questions = qs;
          } catch (e) {
            g.questions = [];
          } finally {
            g.__questionsLoading = false;
          }
        }
      } catch (error) {
        this.$toast.error('Fehler beim Laden der Fragengruppen');
      } finally {
        this.loading = false;
      }
    },
    editGroup(group) {
      if (!group?.id) return;
      this.$router.push(`/question-groups/${group.id}`);
    },
    async deleteGroup(group) {
      if (group.questions_count > 0) {
        this.$toast.error('Gruppe kann nicht gelöscht werden, da sie noch Fragen enthält');
        return;
      }
      if (!confirm(`Möchten Sie die Gruppe "${group.name}" wirklich löschen?`)) {
        return;
      }
      try {
        await this.questionGroupsStore.deleteQuestionGroup(group.id);
        this.$toast.success('Fragengruppe erfolgreich gelöscht');
        await this.loadQuestionGroups();
      } catch (error) {
        this.$toast.error('Fehler beim Löschen der Fragengruppe');
      }
    },

    // Fragen in Gruppen
    openCreateQuestionModal(group) {
      this.currentEditingGroup = group;
      this.currentQuestion = { id: null, type: 'multiple_choice' };
      if (this.$refs.createGroupQuestionModal) {
        // Vorauswahl der Gruppe im Modal
        this.$refs.createGroupQuestionModal.defaultQuestion.question_group_id = group.id;
        this.$refs.createGroupQuestionModal.show();
      }
    },
    openEditQuestionModal(group, question) {
      this.currentEditingGroup = group;
      this.currentQuestion = { ...question };
      if (this.$refs.editGroupQuestionModal) {
        this.$refs.editGroupQuestionModal.show(this.currentQuestion);
      }
    },
    async saveGroupQuestion(questionData, closeModal = true) {
      if (!this.currentEditingGroup || !this.currentEditingGroup.id) return;
      const groupId = this.currentEditingGroup.id;
      const payload = this.prepareGroupQuestionPayload(questionData);
      if (!payload) return;

      this.questionLoading = true;
      try {
        if (questionData.id) {
          await this.questionGroupsStore.updateGroupQuestion(groupId, questionData.id, payload);
        } else {
          await this.questionGroupsStore.createGroupQuestion(groupId, payload);
        }
        // Nach Speichern: Fragenliste der Gruppe neu laden und Count aktualisieren
        const qs = await this.questionGroupsStore.fetchQuestionsByGroup(groupId);
        this.currentEditingGroup.questions = qs;
        this.currentEditingGroup.questions_count = Array.isArray(qs) ? qs.length : 0;

        if (closeModal) {
          if (this.$refs.editGroupQuestionModal) this.$refs.editGroupQuestionModal.hide();
          if (this.$refs.createGroupQuestionModal) this.$refs.createGroupQuestionModal.hide();
        }
        this.$toast.success('Frage gespeichert');
      } catch (error) {
        this.$toast.error('Fehler beim Speichern der Frage');
        console.error(error);
      } finally {
        this.questionLoading = false;
      }
    },
    saveAndAddNextGroupQuestion(questionData) {
      this.saveGroupQuestion(questionData, false).then(() => {
        // Formular zurücksetzen und Modal offen halten
        if (this.$refs.createGroupQuestionModal) {
          this.$refs.createGroupQuestionModal.resetForm();
          this.$refs.createGroupQuestionModal.defaultQuestion.question_group_id = this.currentEditingGroup?.id || null;
          this.$refs.createGroupQuestionModal.show();
        }
      });
    },
    confirmDeleteGroupQuestion(group, question) {
      if (!confirm('Frage wirklich löschen?')) return;
      this.deleteGroupQuestion(group, question);
    },
    async deleteGroupQuestion(group, question) {
      if (!group?.id || !question?.id) return;
      this.questionLoading = true;
      try {
        await this.questionGroupsStore.deleteGroupQuestion(group.id, question.id);
        const qs = await this.questionGroupsStore.fetchQuestionsByGroup(group.id);
        group.questions = qs;
        group.questions_count = Array.isArray(qs) ? qs.length : 0;
        this.$toast.success('Frage gelöscht');
      } catch (error) {
        this.$toast.error('Fehler beim Löschen der Frage');
      } finally {
        this.questionLoading = false;
      }
    },

    // Hilfen
    prepareGroupQuestionPayload(question) {
      if (!question || !question.description || !question.type) {
        this.$toast.error('Bitte füllen Sie alle erforderlichen Felder aus');
        return null;
      }
      const payload = {
        type: question.type,
        description: question.description,
        points: question.type === 'multiple_choice' ? (question.points || this.defaultPointsPerQuestion) : (question.points || 1),
        allow_multiple_answers: !!question.allow_multiple_answers
      };
      if (question.max_points != null) payload.max_points = question.max_points;
      if (question.sort_order != null) payload.sort_order = question.sort_order;
      if (question.image_url) payload.image_url = question.image_url;
      // Antworten 1..6
      for (let i = 1; i <= 6; i++) {
        if (question[`answer_${i}`]) payload[`answer_${i}`] = question[`answer_${i}`];
        if (question[`is_answer_${i}_correct`] !== undefined) payload[`is_answer_${i}_correct`] = !!question[`is_answer_${i}_correct`];
      }
      // Validierung: bei MC mindestens eine richtige
      if (payload.type === 'multiple_choice') {
        const anyCorrect = [1,2,3,4,5,6].some(i => payload[`is_answer_${i}_correct`] === true);
        if (!anyCorrect) {
          this.$toast.error('Bitte markieren Sie mindestens eine korrekte Antwort');
          return null;
        }
      }
      return payload;
    },

    async handleQuestionImageUpdated(file) {
      try {
        const formData = new FormData();
        formData.append('image', file);
        const response = await http.post('/api/upload/question-image', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        const imageUrl = response.data.url;
        this.questionImagePreview = imageUrl;
        if (this.$refs.createGroupQuestionModal) {
          this.$refs.createGroupQuestionModal.defaultQuestion.image_url = imageUrl;
        }
        if (this.$refs.editGroupQuestionModal) {
          this.$refs.editGroupQuestionModal.editableQuestion.image_url = imageUrl;
        }
      } catch (error) {
        console.error('Fehler beim Bildupload:', error);
        this.$toast.error('Fehler beim Hochladen des Bildes');
      }
    },
    handleQuestionImageRemoved() {
      this.questionImagePreview = null;
      if (this.$refs.createGroupQuestionModal) {
        this.$refs.createGroupQuestionModal.defaultQuestion.image_url = null;
      }
      if (this.$refs.editGroupQuestionModal) {
        this.$refs.editGroupQuestionModal.editableQuestion.image_url = null;
      }
    },
    resetQuestionForm() {
      this.currentQuestion = { id: null, type: 'multiple_choice' };
      this.questionImagePreview = null;
    },
    checkForDuplicatesInGroup(questionText) {
      // Einfache Duplikatprüfung innerhalb der aktuell geöffneten Gruppe
      const group = this.currentEditingGroup;
      if (!group || !Array.isArray(group.questions)) return false;
      const normalized = (questionText || '').trim().toLowerCase();
      if (!normalized) return false;
      const exists = group.questions.some(q => (q.description || '').trim().toLowerCase() === normalized);
      if (exists) this.$toast.warning('Ähnliche Frage in dieser Gruppe existiert bereits.');
      return exists;
    },

    closeModal() {
      this.showCreateModal = false;
      this.showEditModal = false;
      this.editingGroup = {
        id: null,
        name: '',
        description: '',
        color: '#007bff'
      };
    },
    formatDate(dateString) {
      if (!dateString) return '–';
      const d = new Date(dateString);
      if (isNaN(d.getTime())) return '–';
      return d.toLocaleDateString('de-DE');
    }
  }
};
</script>

<style lang="scss" scoped>
.question-groups-container {
  background-color: var(--bs-gray-100);
  min-height: 100vh;
  padding-bottom: 3rem;
}

.display-heading {
  font-size: 2.25rem;
  font-weight: 700;
  color: var(--bs-gray-900);
  letter-spacing: -0.02em;
  
  &.with-highlight {
    position: relative;
    padding-bottom: 0.5rem;
    
    &:after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      width: 4rem;
      height: 0.25rem;
      background: linear-gradient(135deg, var(--bs-primary) 0%, var(--bs-primary-dark) 100%);
      border-radius: 1rem;
    }
  }
}

.question-groups-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  gap: 1.25rem;
  padding: 0.25rem;
}

/* Modern Card */
.question-group-card {
  --card-radius: 14px;
  --card-bg: var(--card-bg, #fff);
  --card-border: rgba(0, 0, 0, 0.08);
  --card-shadow: 0 8px 24px rgba(0, 0, 0, 0.06);
  --card-shadow-hover: 0 14px 34px rgba(0, 0, 0, 0.12);

  position: relative;
  border-radius: var(--card-radius);
  background: var(--card-bg);
  border: 1px solid var(--card-border);
  box-shadow: var(--card-shadow);
  padding: 1rem 1rem 0.75rem;
  transition: transform .18s ease, box-shadow .18s ease, border-color .18s ease;
}

.question-group-card:hover {
  transform: translateY(-3px);
  box-shadow: var(--card-shadow-hover);
  border-color: rgba(var(--bs-primary-rgb), 0.28);
}

/* Entfernt visuelle Überlagerungen, die Einrückungsgefühl erzeugten */
.question-group-card::before,
.question-group-card::after {
  display: none;
  content: none;
}

/* Header */
.group-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.group-color-indicator {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  box-shadow: 0 0 0 1px rgba(0,0,0,0.06);
}

.group-info { flex: 1 1 auto; min-width: 0; }

.group-name {
  font-size: 1.075rem;
  font-weight: 800;
  margin: 0 0 0.125rem 0;
  color: var(--bs-gray-900);
}

.group-description {
  font-size: 0.95rem;
  color: var(--bs-gray-600);
  margin: 0;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Footer/Stats */
.group-stats {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem 0.75rem;
  padding-top: 0.85rem;
  margin-top: 0.65rem;
  border-top: 1px solid rgba(0,0,0,0.06);
  flex-wrap: wrap;
}

.group-stats .stat-item { color: var(--bs-gray-700); font-size: 0.95rem; display: flex; align-items: center; gap: .5rem; }
.group-stats .btn { border-radius: 10px; }

/* Schmalere Buttons im Footer */
.group-stats .btn {
  padding: 0.35rem 0.6rem;
  font-size: 0.875rem;
}
.group-stats .btn .icon { margin-right: 0.35rem; }

/* Badge Nuance */
.badge.bg-primary-soft {
  background-color: rgba(var(--bs-primary-rgb), 0.10);
  color: var(--bs-primary);
  border: 1px solid rgba(var(--bs-primary-rgb), 0.2);
}

/* Dark mode friendly */
:deep(.dark-mode) .question-group-card {
  --card-bg: rgba(30, 41, 59, 0.6);
  --card-border: rgba(148, 163, 184, 0.25);
}
</style> 